import { UserManager, WebStorageStateStore, User } from "oidc-client-ts";
import appSettings from "./appSettings";
import { useUserState, hasExternalAccount } from "@/state/user";
import { clearAccount } from "@/state/account";
import { api } from "./backend";

const IMP_USER = "IMP_user";
const IMP_SLUG = "IMP_slug";
const DEL_CA = "DELEGATE_childAcct";
const RETURN_URL = "acct.returnUrl";

class AuthService {
  private userManager: UserManager;
  private storage: Storage = window.sessionStorage;
  private userState = useUserState();
  private isFramed = window.self !== window.top;

  constructor() {
    const origin = window.location.origin;
    const settings = {
      client_id: "acctv2.client",
      response_type: "code",
      scope: "openid profile backend.api offline_access email",
      authority: appSettings.openId.baseUrl,
      redirect_uri: `${origin}/signin-oidc`,
      silent_redirect_uri: `${origin}/silent-oidc.html`,
      post_logout_redirect_uri: origin,
      automaticSilentRenew: true,
      filterProtocolClaims: true,
      loadUserInfo: true,
      monitorSession: true,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    };

    this.userState.initProfile();
    this.userManager = new UserManager(settings);
    this.userManager.clearStaleState();

    this.userManager.events.addSilentRenewError(async (error) => {
      console.warn("SILENT RENEW ERROR", error);
    });

    this.userManager.events.addUserSignedOut(async () => {
      console.log("USER SIGN OUT");

      // NOTE/TODO: We've had issues with infinte loops with the following lines
      // so noting in case it comes up again.
      // This prevents the case where when changing accounts the previous account
      // session is still active. (Impersonation/Integrator session testing)

      const host = window.location.host;
      if (!host.includes("localhost")) {
        await this.userManager.removeUser();
        await this.login(window.location.href);
      }
    });

    this.userManager.events.addUserLoaded(async (authData: any) => {
      console.debug("USER LOADED");
      this.userState.updateUserProfile(authData.profile);
    });

    this.userManager.events.addUserUnloaded(async () => {
      console.log("USER UNLOADED");
      this.userState.initProfile();
    });

    this.userManager.events.addUserSessionChanged(() => {
      console.log("USER SESSION CHANGED");
    });

    this.userManager.events.addUserSignedIn(() => {
      console.log("USER SIGN IN");
    });

    this.userManager.events.addAccessTokenExpiring(async () => {
      console.warn("ACCESS TOKEN EXPIRING");
    });

    this.userManager.events.addAccessTokenExpired(async () => {
      console.warn("ACCESS TOKEN EXPIRED");
      try {
        // Cases where the session is still valid at the identity server
        // but the local refresh token has expired force an iframe auth to use
        // the session cookie.
        console.debug("Access Token Expired - signinSilent");
        await this.userManager.signinSilent({ forceIframeAuth: true });
      } catch (error) {
        console.error(error);
        await this.logout();
      }
    });

    window.addEventListener("storage", async (e) =>
      this.checkDelegateSession(e.key),
    );
  }

  public async checkSession(): Promise<User | null> {
    try {
      const user = await this.userManager.getUser();
      if (user && !user.expired) return user;

      return await this.userManager.signinSilent();
    } catch (error) {
      return null;
    }
  }

  // Used to redirect a open tab that is still pointing to a different child account
  // delegate session. This will redirect to the home page and reload the current child account
  // session.
  private async checkDelegateSession(key: string | null) {
    if (!key || !key.includes("oidc.user")) return;

    const sessionDelegateAcct = this.storage.getItem(DEL_CA);
    if (sessionDelegateAcct) {
      const childAcct = this.userState.user.value?.childAccountId;
      if (childAcct) {
        if (Number(sessionDelegateAcct) !== childAcct) {
          this.storage.setItem(DEL_CA, childAcct.toString());
          document.location.href = "/";
        }
      }
    }
  }

  public async delegate() {
    // force a re-fetch/re-signin of the user in order to get any updated claims
    // required for switching between child accounts when delegating
    await this.userManager.removeUser();
  }

  public impersonate(slug: string | (string | null)[]): void {
    if (typeof slug !== "string") return;

    this.storage.removeItem(IMP_USER);
    this.storage.setItem(IMP_SLUG, slug);
  }

  public getUser(): Promise<User | null> {
    const storedUser = this.storage.getItem(IMP_USER);
    if (storedUser) return Promise.resolve(JSON.parse(storedUser));

    return this.userManager.getUser();
  }

  public async login(returnUrl: string): Promise<void> {
    try {
      if (this.isFramed) {
        // Force iframe auth first to check session cookie. If revoked, throws login-required error,
        // triggering logout and iframe auth error page instead of redirecting to identity server login which
        // does not allow framing. If session is valid, the signinRedirect must still be called below
        // to properly authenticate.
        console.debug("Framed login");
        await this.userManager.signinSilent({ forceIframeAuth: true });
      }

      // Keep track of the return URL for cases where the user clicks
      // sign in then clicks the create account link on the login page.
      if (returnUrl) this.storage.setItem(RETURN_URL, returnUrl);

      await this.userManager.signinRedirect({ state: returnUrl || "/" });
    } catch (error) {
      console.error("Login failed:", error);

      if (this.isFramed) {
        location.href = "/iframe-auth-error";
        return;
      }

      return Promise.reject(error);
    }
  }

  public getReturnUrl(): string | null {
    const returnUrl = this.storage.getItem(RETURN_URL);
    return returnUrl;
  }

  public async logout(): Promise<void> {
    this.storage.removeItem(IMP_SLUG);
    this.storage.removeItem(IMP_USER);
    this.storage.removeItem(DEL_CA);

    clearAccount();
    if (this.isFramed) {
      location.href = "/iframe-auth-error";
      return;
    }

    return this.userManager.signoutRedirect();
  }

  public isImpersonating(): boolean {
    const storedUser = this.storage.getItem(IMP_USER);
    return storedUser ? true : false;
  }

  public async getAccessToken(): Promise<string | null> {
    const storedSlug = this.storage.getItem(IMP_SLUG);
    if (storedSlug) return `imp:${storedSlug}`;

    const user = await this.userManager.getUser();
    return user == null || user.expired || !user.access_token
      ? null
      : user.access_token;
  }

  public async getOrRenewAccessToken(): Promise<string | null> {
    const storedSlug = this.storage.getItem(IMP_SLUG);
    if (storedSlug) return `imp:${storedSlug}`;

    let user = await this.userManager.getUser();

    try {
      if (user == null || user.expired || !user.access_token) {
        user = await this.userManager.signinSilent();
      }
    } catch (error) {
      console.error("Silent sign-in failed:", error);
      await this.logout();
      return null;
    }

    return user == null || user.expired || !user.access_token
      ? null
      : user.access_token;
  }

  public async isLoggedIn(): Promise<boolean> {
    let user = null;

    const storedUser = this.storage.getItem(IMP_USER);
    if (storedUser) {
      user = JSON.parse(storedUser);
      return true;
    }

    const storedSlug = this.storage.getItem(IMP_SLUG);
    if (storedSlug) {
      try {
        const resp = await api.impersonated({
          headers: { Authorization: `Bearer imp:${storedSlug}` },
        });
        const d = resp.data.data;
        if (!d || !d.profile) throw new Error("Impersonation failed");
        user = new User({
          id_token: storedSlug,
          session_state: "",
          access_token: storedSlug,
          refresh_token: storedSlug,
          token_type: "",
          scope: "openid profile app-builder.backend.api",
          expires_at: 0,
          profile: d.profile as any,
          userState: null,
        });
        this.storage.setItem(IMP_USER, JSON.stringify(user));
        return true;
      } catch (error) {
        this.storage.removeItem(IMP_SLUG);
        return false;
      }
    }

    user = await this.checkSession();
    const delegateForAcctId = this.userState.user.value?.childAccountId;
    if (delegateForAcctId)
      this.storage.setItem(DEL_CA, delegateForAcctId.toString());

    return user != null && !user.expired;
  }

  public async getActiveAccountId(): Promise<number | undefined> {
    const [childAcctId, acctId] = await Promise.all([
      this.userState.user.value?.childAccountId,
      this.userState.user.value?.accountId,
    ]);
    return childAcctId ?? acctId;
  }

  public handleCallback() {
    this.userManager
      .signinCallback()
      .then((user) => {
        const redirect = (user?.state as string) || "/";
        location.href =
          user && hasExternalAccount(user.profile)
            ? this.allowedExternalAccountRedirect(redirect)
            : redirect;
      })
      .catch((err) => {
        console.error("handleCallback signin error:", err);
        location.href = "/";
      });
  }

  private allowedExternalAccountRedirect(redirect: string) {
    const allowedPaths = ["/feeds/", "/p/"];

    if (allowedPaths.some((p) => redirect.includes(p))) return redirect;

    return "/";
  }

  public handleSilentRenew() {
    this.userManager.signinCallback();
  }
}

export const auth = new AuthService();
